import React, { ContextType } from 'react';
import { errorContext } from '@contexts/useError/useError';
import { ErrorPage, ShieldsErrorPage } from './pages/ErrorPages';

type ErrorContext = NonNullable<ContextType<typeof errorContext>>;

interface State { error: Error | null; }
interface Props { children: React.ReactNode; }

export class ErrorBoundary extends React.Component<Props, State> {
  static contextType = errorContext;
  declare context: ErrorContext;

  state: State = { error: null };

  static getDerivedStateFromError( error: Error ): State {
    return { error };
  }

  componentDidCatch( error: Error, errorInfo: React.ErrorInfo ) {
    // Log the component stack to the console, not just to Sentry. `handleError`
    // funnels into `Sentry.captureException` + a toast, neither of which is
    // reachable when you are looking over someone's shoulder at a reproduction
    // — and React's own "The above error occurred in..." message is stripped in
    // production builds. Without this, an <ErrorPage /> is indistinguishable
    // from any other <ErrorPage />: you get the error text and no idea which
    // component threw. Naming the component is the whole diagnosis.
    console.error( '[ErrorBoundary] Render error', error, errorInfo.componentStack );
    this.context.handleError( error );
  }

  render() {
    const err = this.state.error;
    if ( err ) {
      if ( err.message.includes( 'CONNECTING state' )) {
        return <ShieldsErrorPage />;
      }
      return <ErrorPage />;
    }
    return this.props.children;
  }
}